import React, { useEffect, useMemo, useRef, useState } from 'react';

const easeOutCubic = (progress) => 1 - ((1 - progress) ** 3);

const AnimatedCount = ({
  value = 0,
  duration = 1200,
  prefix = '',
  suffix = '',
  decimals = 0,
  className = '',
  startWhenVisible = true,
  visibilityThreshold = 0.25
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [hasStarted, setHasStarted] = useState(!startWhenVisible);
  const frameRef = useRef(null);
  const elementRef = useRef(null);

  const targetValue = Number.isFinite(Number(value)) ? Number(value) : 0;

  useEffect(() => {
    if (!startWhenVisible || hasStarted) {
      return undefined;
    }

    if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
      setHasStarted(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          setHasStarted(true);
          observer.disconnect();
        }
      },
      { threshold: visibilityThreshold }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [hasStarted, startWhenVisible, visibilityThreshold]);

  useEffect(() => {
    if (!hasStarted) {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || duration <= 0) {
      setDisplayValue(targetValue);
      return undefined;
    }

    const startTime = performance.now();

    const updateFrame = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);

      setDisplayValue(targetValue * easedProgress);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(updateFrame);
      }
    };

    frameRef.current = requestAnimationFrame(updateFrame);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [duration, hasStarted, targetValue]);

  const formattedValue = useMemo(() => {
    const safeDecimals = Math.max(0, decimals);
    const rounded = safeDecimals > 0
      ? Number(displayValue.toFixed(safeDecimals))
      : Math.round(displayValue);

    return rounded.toLocaleString('en-US', {
      minimumFractionDigits: safeDecimals,
      maximumFractionDigits: safeDecimals
    });
  }, [decimals, displayValue]);

  return (
    <span ref={elementRef} className={className}>
      {prefix}
      {formattedValue}
      {suffix}
    </span>
  );
};

export default AnimatedCount;